/**
 * Available Batches Feature Types
 * Type definitions for US-BATCH-005: View Available Batches
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-005
 */

import type { ProductType, QualityGrade, BatchResponse } from './inventory';

// ============================================
// FILTER AND SORT TYPES
// ============================================

/**
 * Expiry range filter options
 */
export type ExpiryRangeFilter = 'ALL' | 'EXPIRING_SOON' | '30_60_DAYS' | 'OVER_60_DAYS';

/**
 * Sort field options for available batches
 */
export type BatchSortField = 'expiryDate' | 'qualityGrade' | 'availableQuantity' | 'productionDate' | 'pricePerUnit';

/**
 * Sort order options
 */
export type SortOrder = 'ASC' | 'DESC';

/**
 * Filter state for available batches
 */
export interface BatchFilters {
  productType?: ProductType | 'ALL';
  qualityGrades: QualityGrade[];
  pesoCompliant?: boolean | null;
  expiryRange: ExpiryRangeFilter;
  storageLocation: string;
  lowStockOnly?: boolean;
  minPurity?: number; // Minimum methane content percentage (0-100)
  maxPurity?: number; // Maximum methane content percentage (0-100)
  minQuantity?: number; // Minimum biogas volume (m³)
  clusterId?: string; // Production plant/cluster ID
  minH2SContent?: number; // Minimum H2S content (ppm)
  maxH2SContent?: number; // Maximum H2S content (ppm)
}

/**
 * Sort state for available batches
 */
export interface BatchSort {
  field: BatchSortField;
  order: SortOrder;
}

// ============================================
// AVAILABLE BATCH RESPONSE (Enhanced)
// ============================================

/**
 * Stock level indicator
 */
export type StockLevel = 'NORMAL' | 'LOW' | 'CRITICAL';

/**
 * Available batch with stock status and price information
 */
export interface AvailableBatchResponse extends BatchResponse {
  // Stock status fields
  utilizationPercent: number; // 0-100 (percentage of initial quantity used)
  stockLevel: StockLevel; // Calculated based on utilizationPercent

  // Pricing
  pricePerUnit: number;

  // Days remaining until expiry
  daysRemaining: number;

  // Estimated delivery time (calculated from storage location)
  estimatedDeliveryDays?: number;
}

// ============================================
// API REQUEST/RESPONSE
// ============================================

/**
 * Query parameters for fetching available batches
 */
export interface AvailableBatchesParams {
  productType?: ProductType;
  qualityGrade?: QualityGrade;
  pesoCompliant?: boolean;
  sortBy?: BatchSortField;
  sortOrder?: SortOrder;
}

/**
 * API response for available batches endpoint
 */
export interface AvailableBatchesApiResponse {
  success: boolean;
  data: AvailableBatchResponse[];
  message?: string;
}

// ============================================
// COMPONENT PROPS
// ============================================

/**
 * Props for filter preset quick actions
 */
export interface FilterPreset {
  id: string;
  label: string;
  icon?: React.ReactNode;
  filters: Partial<BatchFilters>;
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Determine stock level from utilization percentage
 */
export function getStockLevel(utilizationPercent: number): StockLevel {
  const availablePercent = 100 - utilizationPercent;

  if (availablePercent < 10) return 'CRITICAL';
  if (availablePercent < 20) return 'LOW';
  return 'NORMAL';
}

/**
 * Get stock level color classes
 */
export function getStockLevelColor(level: StockLevel): {
  text: string;
  bg: string;
  border: string;
} {
  switch (level) {
    case 'CRITICAL':
      return {
        text: 'text-red-700',
        bg: 'bg-red-50',
        border: 'border-red-300'
      };
    case 'LOW':
      return {
        text: 'text-orange-700',
        bg: 'bg-orange-50',
        border: 'border-orange-300'
      };
    case 'NORMAL':
      return {
        text: 'text-green-700',
        bg: 'bg-green-50',
        border: 'border-green-300'
      };
  }
}

/**
 * Get stock level label
 */
export function getStockLevelLabel(level: StockLevel): string {
  switch (level) {
    case 'CRITICAL':
      return 'Critical Stock';
    case 'LOW':
      return 'Low Stock';
    case 'NORMAL':
      return 'Normal Stock';
  }
}

/**
 * Filter batches by expiry range
 */
export function filterByExpiryRange(
  batches: AvailableBatchResponse[],
  range: ExpiryRangeFilter
): AvailableBatchResponse[] {
  switch (range) {
    case 'ALL':
      return batches;
    case 'EXPIRING_SOON':
      return batches.filter(b => b.daysRemaining <= 30);
    case '30_60_DAYS':
      return batches.filter(b => b.daysRemaining > 30 && b.daysRemaining <= 60);
    case 'OVER_60_DAYS':
      return batches.filter(b => b.daysRemaining > 60);
    default:
      return batches;
  }
}

/**
 * Sort batches by field and order
 */
export function sortBatches(
  batches: AvailableBatchResponse[],
  field: BatchSortField,
  order: SortOrder
): AvailableBatchResponse[] {
  const sorted = [...batches].sort((a, b) => {
    let comparison = 0;

    switch (field) {
      case 'expiryDate':
        comparison = new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime();
        break;
      case 'qualityGrade':
        // A < B < C
        comparison = a.qualityGrade.localeCompare(b.qualityGrade);
        break;
      case 'availableQuantity':
        comparison = a.availableQuantity - b.availableQuantity;
        break;
      case 'productionDate':
        comparison = new Date(a.productionDate).getTime() - new Date(b.productionDate).getTime();
        break;
      case 'pricePerUnit':
        comparison = a.pricePerUnit - b.pricePerUnit;
        break;
    }

    return order === 'ASC' ? comparison : -comparison;
  });

  return sorted;
}

/**
 * Default filter state
 */
export const DEFAULT_FILTERS: BatchFilters = {
  qualityGrades: [],
  expiryRange: 'ALL',
  storageLocation: '',
  minPurity: undefined,
  maxPurity: undefined,
  minQuantity: undefined,
  clusterId: undefined,
  minH2SContent: undefined,
  maxH2SContent: undefined
};

/**
 * Default sort state (FIFO - oldest expiry first)
 */
export const DEFAULT_SORT: BatchSort = {
  field: 'expiryDate',
  order: 'ASC'
};

/**
 * Expiry range filter options for dropdown
 */
export const EXPIRY_RANGE_OPTIONS = [
  { value: 'ALL', label: 'All Batches' },
  { value: 'EXPIRING_SOON', label: 'Expiring Soon (0-30 days)' },
  { value: '30_60_DAYS', label: '30-60 Days' },
  { value: 'OVER_60_DAYS', label: 'Over 60 Days' }
] as const;

/**
 * Sort field options for dropdown
 */
export const SORT_FIELD_OPTIONS = [
  { value: 'expiryDate', label: 'Expiry Date' },
  { value: 'qualityGrade', label: 'Quality Grade' },
  { value: 'availableQuantity', label: 'Available Quantity' },
  { value: 'productionDate', label: 'Production Date' },
  { value: 'pricePerUnit', label: 'Price' }
] as const;
